import {
  createFormHook,
  createFormHookContexts,
  useSelector,
  type DeepKeys,
  type DeepValue,
} from "@tanstack/react-form"
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type MutableRefObject,
  type ReactNode,
} from "react"

import { unsavedChangesResponse } from "../presentation/responses/unsaved-changes"
import { ApplicationScopeContext } from "./application-context"
import { useLeaveGuard, useSignOutGuard, type LeaveGuard } from "./application-leave-guard"
import { type EditScope, useEditScope, useEditScopeDirty, useEditScopeSnapshot } from "./edit-scope"
import { useDocumentInstance } from "./document-instance"
import { useDocumentLeaveGuard } from "./routed-documents"
import type { FieldControlBinding, FieldControlIssue } from "./field-control-binding"

export type EditSessionMessage = string
export type EditSessionMessageValue = EditSessionMessage | readonly EditSessionMessage[]

type EditSessionFieldPrimitive = string | number | boolean | bigint | symbol | null | undefined | Date
type EditSessionFieldNameAtDepth<TValue, TDepth extends readonly unknown[]> = TDepth["length"] extends 6
  ? never
  : TValue extends EditSessionFieldPrimitive | readonly unknown[]
    ? never
    : TValue extends object
      ? {
          [TKey in Extract<keyof TValue, string>]: EditSessionMemberFieldName<TKey, NonNullable<TValue[TKey]>, TDepth>
        }[Extract<keyof TValue, string>]
      : never

type EditSessionMemberFieldName<
  TPath extends string,
  TMember,
  TDepth extends readonly unknown[],
> = TMember extends EditSessionFieldPrimitive
  ? TPath
  : TMember extends readonly (infer TItem)[]
    ? TPath | EditSessionItemFieldName<`${TPath}.${number}`, NonNullable<TItem>, TDepth>
    : TMember extends object
      ? TPath | `${TPath}.${EditSessionFieldNameAtDepth<TMember, [...TDepth, unknown]>}`
      : TPath

/** Items of an array of objects get indexed names; an array of plain values stays one field. */
type EditSessionItemFieldName<TPath extends string, TItem, TDepth extends readonly unknown[]> = TItem extends
  EditSessionFieldPrimitive | readonly unknown[]
  ? never
  : TItem extends object
    ? TPath | `${TPath}.${EditSessionFieldNameAtDepth<TItem, [...TDepth, unknown]>}`
    : never

/**
 * Dot-separated editable field names, kept Florisoft-owned rather than leaking the form engine's types.
 * Items of an array of objects are addressed by index, for example `lines.${number}.quantity`.
 */
export type EditSessionFieldName<TValues extends object> = EditSessionFieldNameAtDepth<TValues, []>

type EditSessionMemberValue<TValue, TKey extends string> = TValue extends readonly (infer TItem)[]
  ? TKey extends `${number}`
    ? TItem
    : never
  : TKey extends keyof TValue
    ? TValue[TKey]
    : never

export type EditSessionFieldValue<TValues, TName extends string> = TName extends `${infer THead}.${infer TTail}`
  ? EditSessionFieldValue<NonNullable<EditSessionMemberValue<TValues, THead>>, TTail>
  : EditSessionMemberValue<TValues, TName>

type EditSessionArrayFieldNameOf<TValues, TName> = TName extends string
  ? NonNullable<EditSessionFieldValue<TValues, TName>> extends readonly object[]
    ? TName
    : never
  : never

/** Names of the fields that hold an array of objects, the repeating lines a `FieldArray` edits. */
export type EditSessionArrayFieldName<TValues extends object> = EditSessionArrayFieldNameOf<
  TValues,
  EditSessionFieldName<TValues>
>

export type EditSessionArrayItem<TValues, TName extends string> =
  NonNullable<EditSessionFieldValue<TValues, TName>> extends readonly (infer TItem)[] ? TItem : never

export interface EditSessionErrors<TValues extends object> {
  readonly form?: EditSessionMessageValue
  readonly fields?: Partial<Record<EditSessionFieldName<TValues>, EditSessionMessageValue>>
}

export type EditSessionIssue = FieldControlIssue

export interface EditSessionSaveContext {
  /** Revision/version of the authoritative value the submitted draft was based on. */
  readonly baselineRevision: unknown
}

export type EditSessionSaveResult<TValues extends object> =
  | {
      readonly status: "saved"
      /** Canonical value returned by the save operation. Defaults to the submitted value. */
      readonly value?: TValues
      /** New authoritative revision/version when the save operation provides one. */
      readonly revision?: unknown
    }
  | {
      readonly status: "rejected"
      readonly errors: EditSessionErrors<TValues>
    }

export function editSessionSaved<TValues extends object>(
  value?: TValues,
  revision?: unknown
): EditSessionSaveResult<TValues> {
  return value === undefined ? { status: "saved", revision } : { status: "saved", value, revision }
}

export function editSessionRejected<TValues extends object>(
  errors: EditSessionErrors<TValues>
): EditSessionSaveResult<TValues> {
  return { status: "rejected", errors }
}

export type EditSessionValidator<TValues extends object> = (
  value: Readonly<TValues>
) => EditSessionErrors<TValues> | null | undefined

export type EditSessionFieldValidator<TValue, TValues extends object> = (
  value: TValue,
  values: Readonly<TValues>
) => EditSessionMessageValue | null | undefined

export interface EditSessionFieldValidators<TValue, TValues extends object> {
  readonly onChange?: EditSessionFieldValidator<TValue, TValues>
  readonly onBlur?: EditSessionFieldValidator<TValue, TValues>
  readonly onSubmit?: EditSessionFieldValidator<TValue, TValues>
}

export interface EditSessionFieldChange<TValue, TValues extends object> {
  readonly value: TValue
  /** The whole draft after this change. `session.values` still holds the draft of the last render. */
  readonly values: Readonly<TValues>
  readonly session: LoadedEditSession<TValues>
}

export interface EditSessionFieldListeners<TValue, TValues extends object> {
  /**
   * Runs after the user or `setFieldValue` changed this field, not when a baseline is loaded or the draft is reset.
   * Use it to reset or recompute dependent fields with `session.setFieldValue`.
   */
  readonly onChange?: (change: EditSessionFieldChange<TValue, TValues>) => void
}

export interface EditSessionFieldBinding<TValue> extends FieldControlBinding<TValue> {
  readonly errors: readonly EditSessionIssue[]
  readonly isTouched: boolean
  /** True only while the current field value differs from the edit-session baseline. */
  readonly isDirty: boolean
  readonly isValid: boolean
  readonly isValidating: boolean
}

export interface EditSessionFieldProps<TValues extends object, TName extends EditSessionFieldName<TValues>> {
  readonly name: TName
  readonly validators?: EditSessionFieldValidators<EditSessionFieldValue<TValues, TName>, TValues>
  readonly listeners?: EditSessionFieldListeners<EditSessionFieldValue<TValues, TName>, TValues>
  readonly children: (field: EditSessionFieldBinding<EditSessionFieldValue<TValues, TName>>) => ReactNode
}

export type EditSessionFieldComponent<TValues extends object> = <TName extends EditSessionFieldName<TValues>>(
  props: EditSessionFieldProps<TValues, TName>
) => ReactNode

export interface EditSessionFieldArrayItem<TItem> {
  /** Stays with the item when items are inserted, removed or moved. Use it as the React key. */
  readonly key: string
  readonly index: number
  readonly value: TItem
}

export interface EditSessionFieldArray<TItem> {
  readonly name: string
  readonly items: readonly EditSessionFieldArrayItem<TItem>[]
  /** Errors of the array itself, for example from an array validator or a server error on the array name. */
  readonly errors: readonly EditSessionIssue[]
  push(item: TItem): void
  insert(index: number, item: TItem): void
  remove(index: number): void
  move(from: number, to: number): void
}

export interface EditSessionFieldArrayProps<TValues extends object, TName extends EditSessionArrayFieldName<TValues>> {
  readonly name: TName
  readonly validators?: EditSessionFieldValidators<EditSessionFieldValue<TValues, TName>, TValues>
  readonly children: (array: EditSessionFieldArray<EditSessionArrayItem<TValues, TName>>) => ReactNode
}

export type EditSessionFieldArrayComponent<TValues extends object> = <TName extends EditSessionArrayFieldName<TValues>>(
  props: EditSessionFieldArrayProps<TValues, TName>
) => ReactNode

/** The state of one field, as `Field` exposes it, for code that is not inside a `Field` render prop. */
export interface EditSessionFieldState {
  readonly errors: readonly EditSessionIssue[]
  /** True only while the current field value differs from the edit-session baseline. */
  readonly isDirty: boolean
  readonly isTouched: boolean
}

export interface EditSessionSetFieldValueOptions {
  /** Marks the field as touched, as if the user changed it. Default: false, for a computed or reset value. */
  readonly touch?: boolean
}

export interface UseEditSessionOptions<TValues extends object> {
  /**
   * Latest authoritative value supplied by the feature/query. Undefined while it loads: the session then reports
   * `status: "loading"`, renders no fields and does not save. The first defined value becomes the baseline.
   */
  readonly value: TValues | undefined
  /**
   * Stable identity for the supplied value, normally a server version/revision.
   * When omitted, value identity is used.
   */
  readonly revision?: unknown
  /**
   * The record the session edits, such as its id, when one session edits one record after another. When it changes,
   * the session drops its draft, its errors and its baseline and loads again: the first defined `value` after the
   * change becomes the new baseline. Pass `value` undefined until the record of the new key has arrived, so the
   * previous record never becomes its baseline; `useEditSessionSwitch` does that for list-plus-editor screens.
   */
  readonly recordKey?: unknown
  /** Form-level validation performed by TanStack Form during submit. */
  readonly validate?: EditSessionValidator<TValues>
  /**
   * Domain save mapping. Return `editSessionRejected` for expected server-side
   * validation failures; throw only for unexpected infrastructure/application errors.
   */
  readonly save?: (
    value: Readonly<TValues>,
    context: EditSessionSaveContext
  ) => Promise<EditSessionSaveResult<TValues> | void> | EditSessionSaveResult<TValues> | void
  /**
   * Asked before navigation, document close or sign-out while this editing unit is dirty. When omitted, the
   * application's presenter asks to save, discard or cancel (`unsavedChangesResponse`); save leaves only after a
   * successful save. Outside an ApplicationProvider there is no presenter and nothing is asked. False opts out.
   */
  readonly confirmLeave?: LeaveGuard | false
  readonly enableBeforeUnload?: boolean
}

interface EditSessionState<TValues extends object> {
  readonly baselineRevision: unknown
  /** Current draft differs from the accepted baseline. */
  readonly isDirty: boolean
  readonly isTouched: boolean
  readonly isValid: boolean
  readonly isSaving: boolean
  readonly canSave: boolean
  /** New authoritative data arrived while edits were present and has been held back. */
  readonly hasIncomingChanges: boolean
  readonly formErrors: readonly EditSessionIssue[]
  readonly saveError: unknown | null
  /** Renders nothing while the session is loading. */
  readonly Field: EditSessionFieldComponent<TValues>
  /** Repeating lines: an array of objects with stable item keys. Renders nothing while the session is loading. */
  readonly FieldArray: EditSessionFieldArrayComponent<TValues>
  /** Sets a field from outside its render prop, for dependent and computed fields. Ignored while loading. */
  setFieldValue<TName extends EditSessionFieldName<TValues>>(
    name: TName,
    value: EditSessionFieldValue<TValues, TName>,
    options?: EditSessionSetFieldValueOptions
  ): void
  /**
   * The errors, dirty and touched state of one field, whether or not a `Field` renders it. Server errors of a
   * rejected save reach a field that no `Field` renders too. A new function whenever a value or a field state
   * changes, so it is a valid dependency, as `values` is. Empty while loading.
   */
  getFieldState(name: EditSessionFieldName<TValues>): EditSessionFieldState
  /** Flush transient editors, validate, save, and establish a new clean baseline. Resolves false while loading. */
  save(): Promise<boolean>
  /** Discard transient and draft changes. Pending incoming server data becomes the new baseline. */
  reset(): void
  /**
   * Asks to leave the record as navigation does. Resolves true at once when nothing is unsaved; otherwise the leave
   * guard decides, by default the save, discard or cancel choice of `confirmLeave`. Save resolves true only after a
   * successful save, Discard resets the draft, Cancel resolves false.
   */
  requestLeave(): Promise<boolean>
}

export interface LoadedEditSession<TValues extends object> extends EditSessionState<TValues> {
  readonly status: "ready"
  readonly values: Readonly<TValues>
  readonly baseline: Readonly<TValues>
}

/** An edit session whose first value has not arrived. It has no draft, is never dirty and does not save. */
export interface LoadingEditSession<TValues extends object> extends EditSessionState<TValues> {
  readonly status: "loading"
  readonly values: undefined
  readonly baseline: undefined
}

export type EditSession<TValues extends object> = LoadedEditSession<TValues> | LoadingEditSession<TValues>

type IncomingValue<TValues extends object> = {
  readonly value: TValues | undefined
  readonly identity: unknown
  readonly revision: unknown
}

type LoadedIncomingValue<TValues extends object> = IncomingValue<TValues> & { readonly value: TValues }

type SubmitOutcome = "invalid" | "saved"

class EditSessionSubmissionRejected extends Error {
  constructor() {
    super("Edit session save rejected.")
    this.name = "EditSessionSubmissionRejected"
  }
}

/** The subset of TanStack's array field API the field array uses; its own types need the literal field name. */
interface FormArrayField {
  pushValue(value: unknown): void
  insertValue(index: number, value: unknown, options?: { readonly dontValidate?: boolean }): void
  removeValue(index: number): void
  moveValue(from: number, to: number): void
  validate(cause: "change"): unknown
}

/** Field state (errors, touched) of array item fields by form field name. */
type ItemFieldStates = Record<string, unknown>

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/** The field states of the items that still exist, under the index each item has after the change. */
function moveItemFieldStates(
  before: ItemFieldStates,
  arrayName: string,
  oldIndexes: readonly (number | undefined)[]
): ItemFieldStates {
  const newIndexes = new Map<number, number>()
  oldIndexes.forEach((oldIndex, newIndex) => {
    if (oldIndex !== undefined) newIndexes.set(oldIndex, newIndex)
  })
  const itemField = new RegExp(`^${escapeRegExp(arrayName)}\\[(\\d+)\\](.*)$`)
  const moved: ItemFieldStates = {}
  for (const [name, state] of Object.entries(before)) {
    const match = itemField.exec(name)
    const newIndex = match == null ? undefined : newIndexes.get(Number(match[1]))
    if (newIndex !== undefined) moved[`${arrayName}[${newIndex}]${match![2]}`] = state
  }
  return moved
}

/** Whether the session has its first value; Field and FieldArray render nothing until then. */
interface LoadedSignal {
  readonly get: () => boolean
  readonly subscribe: (listener: () => void) => () => void
  readonly markLoaded: () => void
  readonly markLoading: () => void
}

function createLoadedSignal(loaded: boolean): LoadedSignal {
  let current = loaded
  const listeners = new Set<() => void>()
  return {
    get: () => current,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    markLoaded() {
      if (current) return
      current = true
      for (const listener of listeners) listener()
    },
    markLoading() {
      if (!current) return
      current = false
      for (const listener of listeners) listener()
    },
  }
}

const { fieldContext, formContext } = createFormHookContexts()
const { useAppForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {},
  formComponents: {},
})

const allowLeave: LeaveGuard = () => true
const noFieldIssues: readonly EditSessionIssue[] = []
const unchangedFieldState: EditSessionFieldState = { errors: noFieldIssues, isDirty: false, isTouched: false }
const emptyFieldMeta = {
  isValidating: false,
  isTouched: false,
  isBlurred: false,
  isDirty: false,
  errorMap: {},
  errorSourceMap: {},
}
const editSessionOwners = new WeakMap<EditScope, object>()
let lastItemKey = 0

function createItemKey() {
  lastItemKey += 1
  return `item-${lastItemKey}`
}

/** Keeps the keys of items that still exist; items the array gained elsewhere, for example by a reset, get new keys. */
function reconcileItemKeys(keys: readonly string[], length: number): string[] {
  if (keys.length === length) return keys as string[]
  const next = keys.slice(0, length)
  while (next.length < length) next.push(createItemKey())
  return next
}

/**
 * TanStack Form tracks array items as `lines[0].quantity`, and moves field state along with an inserted, removed or
 * moved item only in that notation. Florisoft names stay dot-separated, `lines.0.quantity`.
 */
function toFormFieldName(name: string) {
  return name.replace(/\.(\d+)(?=\.|$)/g, "[$1]")
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype
}

/** The part of the form's field meta that `getFieldState` reads. */
interface EditSessionFieldMeta {
  readonly errors: readonly unknown[]
  readonly isTouched: boolean
  readonly isDefaultValue: boolean
}

/** Reads a dot-separated field name, `lines.0.quantity`, from a value. */
function getFieldValueAt(value: unknown, name: string): unknown {
  let current = value
  for (const segment of name.split(".")) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[segment]
  }
  return current
}

function editSessionValuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true
  if (left instanceof Date && right instanceof Date) return left.getTime() === right.getTime()
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((value, index) => editSessionValuesEqual(value, right[index]))
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left)
    const rightKeys = Object.keys(right)
    return (
      leftKeys.length === rightKeys.length &&
      leftKeys.every((key) => key in right && editSessionValuesEqual(left[key], right[key]))
    )
  }
  return false
}

function rebaseConcurrentChanges<TValue>(saved: TValue, submitted: TValue, current: TValue): TValue {
  if (Object.is(current, submitted)) return saved

  if (current instanceof Date && submitted instanceof Date && saved instanceof Date) {
    return current.getTime() === submitted.getTime() ? saved : current
  }

  if (Array.isArray(current) && Array.isArray(submitted) && Array.isArray(saved)) {
    if (current.length !== submitted.length || saved.length !== submitted.length) return current
    return current.map((value, index) => rebaseConcurrentChanges(saved[index], submitted[index], value)) as TValue
  }

  if (isPlainObject(current) && isPlainObject(submitted) && isPlainObject(saved)) {
    const result: Record<string, unknown> = { ...saved }
    const keys = new Set([...Object.keys(submitted), ...Object.keys(current)])
    for (const key of keys) {
      if (!(key in current)) {
        delete result[key]
        continue
      }
      if (!(key in submitted)) {
        result[key] = current[key]
        continue
      }
      result[key] = rebaseConcurrentChanges(saved[key], submitted[key], current[key])
    }
    return result as TValue
  }

  return current
}

function normalizeIssues(value: unknown): readonly EditSessionIssue[] {
  const messages: string[] = []
  const visit = (candidate: unknown) => {
    if (candidate == null || candidate === false) return
    if (Array.isArray(candidate)) {
      for (const nested of candidate) visit(nested)
      return
    }
    if (typeof candidate === "string") {
      if (candidate.length > 0) messages.push(candidate)
      return
    }
    if (candidate instanceof Error) {
      if (candidate.message) messages.push(candidate.message)
      return
    }
    if (typeof candidate === "object" && "message" in candidate) {
      visit((candidate as { readonly message?: unknown }).message)
    }
  }
  visit(value)
  return [...new Set(messages)].map((message) => ({ message }))
}

function normalizeMessageValue(value: EditSessionMessageValue | null | undefined) {
  if (value == null) return undefined
  if (typeof value === "string") return value.length > 0 ? value : undefined
  const values = [...new Set(value.filter((message) => message.length > 0))]
  if (values.length === 0) return undefined
  return values.length === 1 ? values[0] : values
}

function toTanStackErrors<TValues extends object>(errors: EditSessionErrors<TValues> | null | undefined) {
  if (errors == null) return undefined
  const form = normalizeMessageValue(errors.form)
  const fields: Partial<Record<DeepKeys<TValues>, EditSessionMessageValue>> = {}
  for (const [name, messages] of Object.entries(errors.fields ?? {}) as Array<
    [string, EditSessionMessageValue | undefined]
  >) {
    const normalized = normalizeMessageValue(messages)
    if (normalized !== undefined) fields[toFormFieldName(name) as DeepKeys<TValues>] = normalized
  }
  if (form === undefined && Object.keys(fields).length === 0) return undefined
  return { form, fields }
}

function toFieldValidators<TValue, TValues extends object>(
  validators: EditSessionFieldValidators<TValue, TValues> | undefined,
  currentValues: () => Readonly<TValues>
) {
  const run = (validator: EditSessionFieldValidator<TValue, TValues> | undefined) =>
    validator == null ? undefined : ({ value }: { value: unknown }) => validator(value as TValue, currentValues())
  return {
    onChange: run(validators?.onChange),
    onBlur: run(validators?.onBlur),
    onSubmit: run(validators?.onSubmit),
  }
}

interface ItemFieldStateForm {
  setFieldMeta(name: never, meta: never): void
}

/**
 * Items keep their React key when they move to another index, so their fields get a new name. The form clears the
 * state of a name whose previous field unmounts, and moves item state only for fields it renders by index. Rendered
 * after the items, this puts each item's field state under its new index once the items have committed.
 */
function ItemFieldStateRestore({
  form,
  itemStates,
}: {
  readonly form: ItemFieldStateForm
  readonly itemStates: MutableRefObject<ItemFieldStates | null>
}) {
  useLayoutEffect(() => {
    const states = itemStates.current
    if (states == null) return
    itemStates.current = null
    for (const [name, meta] of Object.entries(states)) form.setFieldMeta(name as never, meta as never)
  })
  return null
}

/**
 * Owns one record-editing unit. When an EditScope is present, this session owns
 * that scope's transient editors; independent editors/dialogs need their own
 * nested EditScopeProvider.
 */
export function useEditSession<TValues extends object>(
  options: UseEditSessionOptions<TValues> & { readonly value: TValues }
): LoadedEditSession<TValues>
export function useEditSession<TValues extends object>(options: UseEditSessionOptions<TValues>): EditSession<TValues>
export function useEditSession<TValues extends object>(options: UseEditSessionOptions<TValues>): EditSession<TValues> {
  const optionsRef = useRef(options)
  optionsRef.current = options
  const editScope = useEditScope()
  const editScopeSnapshot = useEditScopeSnapshot()
  const document = useDocumentInstance()
  const presenter = useContext(ApplicationScopeContext)?.presenter
  const sessionOwner = useMemo(() => ({}), [])
  const initialIdentity = options.revision ?? options.value
  const latestSourceRef = useRef<IncomingValue<TValues>>({
    value: options.value,
    identity: initialIdentity,
    revision: options.revision,
  })
  latestSourceRef.current = {
    value: options.value,
    identity: options.revision ?? options.value,
    revision: options.revision,
  }
  const seenIdentityRef = useRef<unknown>(initialIdentity)
  const pendingIncomingRef = useRef<LoadedIncomingValue<TValues> | null>(null)
  const [baseline, setBaseline] = useState(options.value)
  const baselineRef = useRef(baseline)
  baselineRef.current = baseline
  const [baselineRevision, setBaselineRevision] = useState(options.revision)
  const baselineRevisionRef = useRef(baselineRevision)
  baselineRevisionRef.current = baselineRevision
  const [hasIncomingChanges, setHasIncomingChanges] = useState(false)
  const [saveError, setSaveError] = useState<unknown | null>(null)
  const submitOutcomeRef = useRef<SubmitOutcome>("invalid")
  // The record the baseline belongs to, or that the session loads. A render with another recordKey is loading at
  // once, before the effect below drops the previous record.
  const [baselineRecordKey, setBaselineRecordKey] = useState(options.recordKey)
  const recordKeyRef = useRef(options.recordKey)
  const isLoaded = baseline !== undefined && Object.is(baselineRecordKey, options.recordKey)
  const [loadedSignal] = useState(() => createLoadedSignal(isLoaded))

  const form = useAppForm({
    // The edit session, not the still-refreshing query prop, owns the accepted
    // baseline. This also prevents a successful canonical save result from
    // being overwritten before the backing query publishes its next revision.
    // Until the first value arrives there are no values at all, never placeholders.
    defaultValues: baseline as TValues,
    validators: {
      onSubmit: ({ value }) => toTanStackErrors(optionsRef.current.validate?.(value)),
    },
    onSubmitInvalid: () => {
      submitOutcomeRef.current = "invalid"
    },
    onSubmit: async ({ value, formApi }) => {
      const saveOperation = optionsRef.current.save
      if (saveOperation == null) return
      const submittedValue = value
      const submittedBaselineRevision = baselineRevisionRef.current
      const submittedRecordKey = recordKeyRef.current
      const result = await saveOperation(value, { baselineRevision: submittedBaselineRevision })
      // The session moved to another record while this one saved: the outcome belongs to the record it left.
      if (!Object.is(submittedRecordKey, recordKeyRef.current)) {
        submitOutcomeRef.current = result?.status === "rejected" ? "invalid" : "saved"
        return
      }
      if (result?.status === "rejected") {
        const errors = toTanStackErrors(result.errors)
        // The form puts errors only on fields it has state for. A field that no Field renders, such as a property
        // of a property grid, gets that state here, so its error shows through getFieldState.
        for (const name of Object.keys(errors?.fields ?? {}) as DeepKeys<TValues>[]) {
          if (formApi.state.fieldMetaBase[name] !== undefined) continue
          formApi.getFieldInfo(name)
          formApi.setFieldMeta(name, (previous) => previous ?? (emptyFieldMeta as typeof previous))
        }
        formApi.setErrorMap({
          ...formApi.state.errorMap,
          onSubmit: errors,
        })
        submitOutcomeRef.current = "invalid"
        throw new EditSessionSubmissionRejected()
      }

      const savedValue = result?.status === "saved" && result.value !== undefined ? result.value : value
      const currentValue = formApi.state.values
      const rebasedValue = rebaseConcurrentChanges(savedValue, submittedValue, currentValue)
      const savedRevision =
        result?.status === "saved" && "revision" in result
          ? (result.revision ?? submittedBaselineRevision)
          : submittedBaselineRevision
      pendingIncomingRef.current = null
      setHasIncomingChanges(false)
      setBaseline(savedValue)
      baselineRef.current = savedValue
      setBaselineRevision(savedRevision)
      baselineRevisionRef.current = savedRevision
      formApi.reset(savedValue)
      if (!Object.is(rebasedValue, savedValue)) {
        formApi.reset(rebasedValue, { keepDefaultValues: true })
      }
      submitOutcomeRef.current = "saved"
    },
  })

  const values = useSelector(form.store, (state) => state.values as TValues | undefined)
  const isDefaultValue = useSelector(form.store, (state) => state.isDefaultValue)
  const isTouched = useSelector(form.store, (state) => state.isTouched)
  const isValid = useSelector(form.store, (state) => state.isValid)
  const isSaving = useSelector(form.store, (state) => state.isSubmitting)
  const formSubmitErrors = useSelector(form.store, (state) => state.errorMap.onSubmit)
  const fieldMeta = useSelector(form.store, (state) => state.fieldMeta)
  const isDirty = isLoaded && !isDefaultValue

  useEditScopeDirty(isDirty)

  useLayoutEffect(() => {
    if (editScope == null) return
    const existingOwner = editSessionOwners.get(editScope)
    if (existingOwner != null && existingOwner !== sessionOwner) {
      throw new Error(
        "An EditScope can host only one EditSession. Wrap an independent editor or dialog in its own EditScopeProvider."
      )
    }
    editSessionOwners.set(editScope, sessionOwner)
    return () => {
      if (editSessionOwners.get(editScope) === sessionOwner) editSessionOwners.delete(editScope)
    }
  }, [editScope, sessionOwner])

  const adoptIncoming = useCallback(
    (incoming: LoadedIncomingValue<TValues>) => {
      pendingIncomingRef.current = null
      setHasIncomingChanges(false)
      setSaveError(null)
      setBaseline(incoming.value)
      baselineRef.current = incoming.value
      setBaselineRevision(incoming.revision)
      baselineRevisionRef.current = incoming.revision
      form.reset(incoming.value)
      loadedSignal.markLoaded()
    },
    [form, loadedSignal]
  )

  // Another record: its draft, errors and baseline go, and the session loads. A value that came with the new key
  // becomes the baseline at once; otherwise the effect below takes the first value that arrives.
  useLayoutEffect(() => {
    if (Object.is(options.recordKey, recordKeyRef.current)) return
    recordKeyRef.current = options.recordKey
    setBaselineRecordKey(options.recordKey)
    editScope?.discardTransientEdits()
    const incoming = latestSourceRef.current
    seenIdentityRef.current = incoming.identity
    if (incoming.value !== undefined) {
      adoptIncoming(incoming as LoadedIncomingValue<TValues>)
      return
    }
    pendingIncomingRef.current = null
    setHasIncomingChanges(false)
    setSaveError(null)
    setBaseline(undefined)
    baselineRef.current = undefined
    setBaselineRevision(undefined)
    baselineRevisionRef.current = undefined
    form.reset()
    loadedSignal.markLoading()
  }, [adoptIncoming, editScope, form, loadedSignal, options.recordKey])

  // The first value becomes the baseline before paint, so a form whose data arrived never shows a loading frame.
  useLayoutEffect(() => {
    const incoming = latestSourceRef.current
    if (baselineRef.current !== undefined || incoming.value === undefined) return
    seenIdentityRef.current = incoming.identity
    adoptIncoming(incoming as LoadedIncomingValue<TValues>)
  }, [adoptIncoming, options.value])

  useEffect(() => {
    const incoming = latestSourceRef.current
    // A loaded session keeps its baseline while the source reloads.
    if (incoming.value === undefined) return
    if (Object.is(seenIdentityRef.current, incoming.identity)) return
    seenIdentityRef.current = incoming.identity
    const loadedIncoming = incoming as LoadedIncomingValue<TValues>

    // A query commonly republishes the canonical value returned by our own
    // save. That confirms the current baseline even when the user has already
    // started another local edit; it is not an external conflicting refresh.
    if (editSessionValuesEqual(incoming.value, baselineRef.current)) {
      if (incoming.revision !== undefined) {
        setBaselineRevision(incoming.revision)
        baselineRevisionRef.current = incoming.revision
      }
      return
    }

    if (!isDirty && !editScopeSnapshot.hasTransientChanges) {
      adoptIncoming(loadedIncoming)
      return
    }

    pendingIncomingRef.current = loadedIncoming
    setHasIncomingChanges(true)
  }, [adoptIncoming, editScopeSnapshot.hasTransientChanges, isDirty, options.revision, options.value])

  useEffect(() => {
    const pending = pendingIncomingRef.current
    if (pending == null || isDirty || editScopeSnapshot.hasTransientChanges) return
    adoptIncoming(pending)
  }, [adoptIncoming, editScopeSnapshot.hasTransientChanges, isDirty])

  const reset = useCallback(() => {
    if (baselineRef.current === undefined || form.state.isSubmitting) return
    editScope?.discardTransientEdits()
    const pending = pendingIncomingRef.current
    if (pending != null) {
      seenIdentityRef.current = pending.identity
      adoptIncoming(pending)
      return
    }
    setSaveError(null)
    form.reset(baselineRef.current)
  }, [adoptIncoming, editScope, form])

  const save = useCallback(async () => {
    if (baselineRef.current === undefined || optionsRef.current.save == null || form.state.isSubmitting) return false
    if (editScope != null && !editScope.flushTransientEdits()) return false
    setSaveError(null)
    submitOutcomeRef.current = "invalid"
    // A rendered field drops the previous save's errors when it validates on submit. A field that no Field renders
    // does not validate, so its errors are dropped here; otherwise they would block every next save.
    const fieldMetaBase = form.state.fieldMetaBase
    for (const name of Object.keys(fieldMetaBase) as DeepKeys<TValues>[]) {
      if (form.fieldInfo[name]?.instance != null || fieldMetaBase[name]?.errorSourceMap?.onSubmit !== "form") continue
      form.setFieldMeta(name, (previous) => ({
        ...previous,
        errorMap: { ...previous.errorMap, onSubmit: undefined },
        errorSourceMap: { ...previous.errorSourceMap, onSubmit: undefined },
      }))
    }
    try {
      await form.handleSubmit()
      return (submitOutcomeRef.current as SubmitOutcome) === "saved"
    } catch (error) {
      if (error instanceof EditSessionSubmissionRejected) return false
      setSaveError(error)
      return false
    }
  }, [editScope, form])

  const askToLeave = useCallback<LeaveGuard>(async () => {
    if (presenter == null) return true
    const choice = await presenter.present(unsavedChangesResponse, { canSave: optionsRef.current.save != null })
    if (choice === "save") return save()
    if (choice === "discard") {
      reset()
      return true
    }
    return false
  }, [presenter, reset, save])

  const leaveGuard =
    options.confirmLeave === false ? null : (options.confirmLeave ?? (presenter == null ? null : askToLeave))
  const guardEnabled = leaveGuard != null && (editScope != null ? editScopeSnapshot.isDirty : isDirty)
  useDocumentLeaveGuard(leaveGuard ?? allowLeave, {
    enabled: document != null && guardEnabled,
    enableBeforeUnload: options.enableBeforeUnload ?? true,
  })
  useLeaveGuard(leaveGuard ?? allowLeave, {
    enabled: document == null && guardEnabled,
    enableBeforeUnload: options.enableBeforeUnload ?? true,
  })
  // A document's workspace asks its leave guards before sign-out; an edit session outside one asks itself.
  useSignOutGuard(leaveGuard ?? allowLeave, { enabled: document == null && guardEnabled })

  const leaveStateRef = useRef({ leaveGuard, unsaved: false })
  leaveStateRef.current = { leaveGuard, unsaved: editScope != null ? editScopeSnapshot.isDirty : isDirty }
  const requestLeave = useCallback(async () => {
    const { leaveGuard, unsaved } = leaveStateRef.current
    if (!unsaved || leaveGuard == null) return true
    return leaveGuard()
  }, [])

  const setFieldValue = useCallback(
    (name: string, value: unknown, setOptions?: EditSessionSetFieldValueOptions) => {
      if (baselineRef.current === undefined) return
      const formName = toFormFieldName(name) as DeepKeys<TValues>
      const touch = setOptions?.touch === true
      form.setFieldValue(formName, value as DeepValue<TValues, DeepKeys<TValues>>, {
        dontUpdateMeta: !touch,
        dontValidate: !touch,
      })
      // Validating through the form marks the field touched; a computed value is validated without that.
      if (!touch) void form.fieldInfo[formName]?.instance?.validate("change")
    },
    [form]
  ) as EditSessionState<TValues>["setFieldValue"]

  const getFieldState = useCallback(
    (name: string): EditSessionFieldState => {
      if (values === undefined || baseline === undefined) return unchangedFieldState
      const formName = toFormFieldName(name)
      const meta = (fieldMeta as Record<string, EditSessionFieldMeta | undefined>)[formName]
      // A field that was never touched or validated has no state; only a computed value can have changed it.
      if (meta == null) {
        const isDirty = !editSessionValuesEqual(getFieldValueAt(values, name), getFieldValueAt(baseline, name))
        return isDirty ? { ...unchangedFieldState, isDirty } : unchangedFieldState
      }
      return { errors: normalizeIssues(meta.errors), isDirty: !meta.isDefaultValue, isTouched: meta.isTouched }
    },
    [baseline, fieldMeta, values]
  ) as EditSessionState<TValues>["getFieldState"]

  const sessionRef = useRef<EditSession<TValues> | null>(null)

  const Field = useMemo<EditSessionFieldComponent<TValues>>(() => {
    const currentValues = () => form.state.values
    function EditField<TName extends EditSessionFieldName<TValues>>({
      name,
      validators,
      listeners,
      children,
    }: EditSessionFieldProps<TValues, TName>) {
      const isLoaded = useSyncExternalStore(loadedSignal.subscribe, loadedSignal.get, loadedSignal.get)
      if (!isLoaded) return null
      const onChange = listeners?.onChange
      return (
        <form.AppField
          name={toFormFieldName(name) as DeepKeys<TValues>}
          validators={toFieldValidators(validators, currentValues)}
          listeners={
            onChange == null
              ? undefined
              : {
                  onChange: ({ value }) =>
                    onChange({
                      value: value as EditSessionFieldValue<TValues, TName>,
                      values: form.state.values,
                      session: sessionRef.current as LoadedEditSession<TValues>,
                    }),
                }
          }
        >
          {(field) =>
            children({
              name,
              value: field.state.value as EditSessionFieldValue<TValues, TName>,
              errors: normalizeIssues(field.state.meta.errors),
              isTouched: field.state.meta.isTouched,
              isDirty: !field.state.meta.isDefaultValue,
              isValid: field.state.meta.isValid,
              isValidating: field.state.meta.isValidating,
              setValue: (value) => field.handleChange(value as DeepValue<TValues, DeepKeys<TValues>>),
              blur: () => field.handleBlur(),
            })
          }
        </form.AppField>
      )
    }
    return EditField
  }, [form, loadedSignal])

  const FieldArray = useMemo<EditSessionFieldArrayComponent<TValues>>(() => {
    const currentValues = () => form.state.values
    function EditFieldArray<TName extends EditSessionArrayFieldName<TValues>>({
      name,
      validators,
      children,
    }: EditSessionFieldArrayProps<TValues, TName>) {
      const isLoaded = useSyncExternalStore(loadedSignal.subscribe, loadedSignal.get, loadedSignal.get)
      const keysRef = useRef<string[]>([])
      const itemStatesRef = useRef<ItemFieldStates | null>(null)
      if (!isLoaded) return null
      const formName = toFormFieldName(name)
      /**
       * Applies one change to the item keys and runs the form operation. The field state of every item goes to the
       * item's new index, see ItemFieldStateRestore.
       */
      const changeItems = (change: <T>(items: T[], created: T) => void, operate: () => void) => {
        const oldIndexes: (number | undefined)[] = keysRef.current.map((_, index) => index)
        change(oldIndexes, undefined)
        const keys = [...keysRef.current]
        change(keys, createItemKey())
        keysRef.current = keys
        const before = form.state.fieldMetaBase as Record<string, unknown>
        operate()
        itemStatesRef.current = moveItemFieldStates(before, formName, oldIndexes)
      }
      return (
        <form.AppField name={formName as DeepKeys<TValues>} validators={toFieldValidators(validators, currentValues)}>
          {(field) => {
            const arrayField = field as unknown as FormArrayField
            const value: unknown = field.state.value
            const items = (Array.isArray(value) ? value : []) as readonly EditSessionArrayItem<TValues, TName>[]
            const keys = reconcileItemKeys(keysRef.current, items.length)
            keysRef.current = keys
            return (
              <>
                {children({
                  name,
                  items: items.map((item, index) => ({ key: keys[index]!, index, value: item })),
                  errors: normalizeIssues(field.state.meta.errors),
                  push: (item) =>
                    changeItems(
                      (entries, created) => entries.push(created),
                      () => arrayField.pushValue(item)
                    ),
                  insert: (index, item) => {
                    // With validation the form would move item state only after awaiting it, after the restore.
                    changeItems(
                      (entries, created) => entries.splice(index, 0, created),
                      () => arrayField.insertValue(index, item, { dontValidate: true })
                    )
                    void arrayField.validate("change")
                  },
                  remove: (index) =>
                    changeItems(
                      (entries) => entries.splice(index, 1),
                      () => arrayField.removeValue(index)
                    ),
                  move: (from, to) =>
                    changeItems(
                      (entries) => entries.splice(to, 0, ...entries.splice(from, 1)),
                      () => arrayField.moveValue(from, to)
                    ),
                })}
                <ItemFieldStateRestore form={form} itemStates={itemStatesRef} />
              </>
            )
          }}
        </form.AppField>
      )
    }
    return EditFieldArray
  }, [form, loadedSignal])

  const state: EditSessionState<TValues> = {
    baselineRevision,
    isDirty,
    isTouched,
    isValid,
    isSaving,
    canSave: isLoaded && options.save != null && !isSaving && (isDirty || editScopeSnapshot.hasTransientChanges),
    hasIncomingChanges,
    formErrors: normalizeIssues(formSubmitErrors),
    saveError,
    Field,
    FieldArray,
    setFieldValue,
    getFieldState,
    save,
    reset,
    requestLeave,
  }
  const session: EditSession<TValues> =
    isLoaded && values !== undefined
      ? { ...state, status: "ready", values, baseline }
      : { ...state, status: "loading", values: undefined, baseline: undefined }
  sessionRef.current = session
  return session
}
